// Chakra Imports
import {
  Avatar,
  Box,
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useColorModeValue,
  useColorMode,
} from '@chakra-ui/react';
// Custom Components
import { ItemContent } from 'components/menu/ItemContent';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import { SidebarResponsive } from 'components/sidebar/Sidebar';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { usePermissions } from 'hooks/usePermissions';
import { CLINIC_BRANCHES, isCurrentBranch } from 'constants/branches';
import { MdNotificationsNone, MdInfoOutline, MdStorefront } from 'react-icons/md';
import {
  markConsultCallIdsSeen,
  playConsultCallChime,
  readSeenConsultCallIds,
} from 'utils/consultCallChime';
import { IoMdMoon, IoMdSunny } from 'react-icons/io';
import routes from 'routes';
import { PERMISSIONS } from 'constants/permissions';
import { notificationsApi } from 'api';
import { NotificationMenuItem } from 'components/navbar/NotificationMenuItem';
import { LedgerSystemIndicator } from 'components/ledger/LedgerSystemIndicator';
export default function HeaderLinks(props) {
  const { secondary } = props;
  const { colorMode, toggleColorMode } = useColorMode();
  const { logout, isAuthenticated, user } = useAuth();
  const { isDirector, can } = usePermissions();
  const canViewNotifications =
    can(PERMISSIONS.DASHBOARD) || can(PERMISSIONS.MEDICAL_RECORDS) || can(PERMISSIONS.SCHEDULE);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);

  const currentHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const currentBranch =
    CLINIC_BRANCHES.find((b) => isCurrentBranch(b.url, currentHost)) || CLINIC_BRANCHES[0];

  useEffect(() => {
    if (!isAuthenticated || !canViewNotifications) return undefined;
    const load = () => {
      notificationsApi
        .list()
        .then((data) => {
          const items = Array.isArray(data?.items) ? data.items : [];
          setNotifications(items);
          const soundIds = items
            .filter((item) => item?.playSound && item.id)
            .map((item) => String(item.id));
          if (soundIds.length === 0) return;
          const seen = readSeenConsultCallIds(user?.id);
          const fresh = soundIds.filter((id) => !seen.has(id));
          if (fresh.length > 0) {
            playConsultCallChime();
            markConsultCallIdsSeen(user?.id, fresh);
          }
        })
        .catch(() => setNotifications([]));
    };
    load();
    const id = setInterval(load, 20_000);
    return () => clearInterval(id);
  }, [isAuthenticated, canViewNotifications, user?.id]);

  const handleLogout = () => {
    logout();
    navigate('/auth/sign-in', { replace: true });
  };
  // Chakra Color Mode
  const navbarIcon = useColorModeValue('gray.400', 'white');
  let menuBg = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorBrand = useColorModeValue('brand.700', 'brand.400');
  const borderColor = useColorModeValue('#E6ECFA', 'rgba(135, 140, 189, 0.3)');
  const shadow = useColorModeValue(
    '14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
    '14px 17px 40px 4px rgba(112, 144, 176, 0.06)',
  );
  return (
    <Flex
      w={{ sm: '100%', md: 'auto' }}
      alignItems="center"
      flexDirection="row"
      bg={menuBg}
      flexWrap={secondary ? { base: 'wrap', md: 'nowrap' } : 'unset'}
      p="10px"
      borderRadius="30px"
      boxShadow={shadow}
    >
      <Box display={{ base: 'block', md: 'none' }} me="10px">
        <LedgerSystemIndicator />
      </Box>
      <SearchBar
        mb={() => {
          if (secondary) {
            return { base: '10px', md: 'unset' };
          }
          return 'unset';
        }}
        me="10px"
        borderRadius="30px"
      />
      {isDirector && CLINIC_BRANCHES.length > 0 ? (
        <Menu>
          <MenuButton
            as={Button}
            variant="no-hover"
            bg="transparent"
            p="0px"
            minW="unset"
            minH="unset"
            h="auto"
            w="max-content"
            me="10px"
          >
            <Flex align="center" gap="4px">
              <Icon mt="2px" h="18px" w="18px" color={navbarIcon} as={MdStorefront} />
              <Text
                fontSize="xs"
                fontWeight="600"
                color={navbarIcon}
                display={{ base: 'none', md: 'block' }}
              >
                {currentBranch?.label}
              </Text>
            </Flex>
          </MenuButton>
          <MenuList
            boxShadow={shadow}
            p="10px"
            borderRadius="16px"
            bg={menuBg}
            border="none"
            mt="10px"
            minW="200px"
          >
            <Text fontSize="xs" fontWeight="700" color={textColor} px="14px" pb="8px">
              Выбрать филиал
            </Text>
            {CLINIC_BRANCHES.map((branch) => {
              const active = isCurrentBranch(branch.url, currentHost);
              return (
                <MenuItem
                  key={branch.url}
                  borderRadius="8px"
                  px="14px"
                  py="8px"
                  bg={active ? 'brand.50' : 'transparent'}
                  _hover={{ bg: 'secondaryGray.100' }}
                  _focus={{ bg: 'secondaryGray.100' }}
                  onClick={() => {
                    if (!active) window.location.href = branch.url;
                  }}
                >
                  <Flex align="center" gap="8px">
                    <Icon
                      as={MdStorefront}
                      w="14px"
                      h="14px"
                      color={active ? 'brand.500' : textColor}
                    />
                    <Text
                      fontSize="sm"
                      fontWeight={active ? '700' : '400'}
                      color={active ? 'brand.500' : textColor}
                    >
                      {branch.label}
                    </Text>
                  </Flex>
                </MenuItem>
              );
            })}
          </MenuList>
        </Menu>
      ) : null}
      <SidebarResponsive routes={routes} />
      <Menu>
        <MenuButton p="0px">
          <Box position="relative" me="10px" mt="6px">
            <Icon
              as={MdNotificationsNone}
              color={navbarIcon}
              w="18px"
              h="18px"
            />
            {notifications.some((item) => item.type === 'consult_call') ? (
              <Box
                position="absolute"
                top="-2px"
                right="-2px"
                w="8px"
                h="8px"
                bg="orange.400"
                borderRadius="full"
              />
            ) : null}
          </Box>
        </MenuButton>
        <MenuList
          boxShadow={shadow}
          p="20px"
          borderRadius="20px"
          bg={menuBg}
          border="none"
          mt="22px"
          me={{ base: '30px', md: 'unset' }}
          minW={{ base: 'unset', md: '400px', xl: '450px' }}
          maxW={{ base: '360px', md: 'unset' }}
        >
          <Flex w="100%" mb="20px">
            <Text fontSize="md" fontWeight="600" color={textColor}>
              Уведомления
            </Text>
            <Text
              fontSize="sm"
              fontWeight="500"
              color={textColorBrand}
              ms="auto"
              cursor="pointer"
            >
              Прочитать все
            </Text>
          </Flex>
          <Flex flexDirection="column" maxH="360px" overflowY="auto">
            {notifications.length === 0 ? (
              <Text fontSize="sm" color={textColor} py={4}>
                Нет новых уведомлений
              </Text>
            ) : (
              notifications.map((item) => (
                <Box key={item.id} mb={2}>
                  <NotificationMenuItem item={item} onNavigate={(link) => navigate(link)} />
                </Box>
              ))
            )}
          </Flex>
        </MenuList>
      </Menu>

      <Menu>
        <MenuButton p="0px">
          <Icon
            mt="6px"
            as={MdInfoOutline}
            color={navbarIcon}
            w="18px"
            h="18px"
            me="10px"
          />
        </MenuButton>
        <MenuList
          boxShadow={shadow}
          p="20px"
          me={{ base: '30px', md: 'unset' }}
          borderRadius="20px"
          bg={menuBg}
          border="none"
          mt="22px"
          minW={{ base: 'unset' }}
          maxW={{ base: '360px', md: 'unset' }}
        >
          <Flex flexDirection="column" gap="8px">
            <Text fontSize="sm" fontWeight="700" color={textColor} mb="4px">
              DEM CLINIC CRM
            </Text>
            <Text fontSize="xs" color={textColorBrand}>
              Система для управления записью, врачами и пациентами DEM CLINIC.
            </Text>
            <Text fontSize="xs" color={textColor}>
              Версия: 1.0.0
            </Text>
            <Text fontSize="xs" color={textColor}>
              Рабочие часы: 10:00 – 22:00
            </Text>
            <Text fontSize="xs" color={textColor}>
              Ответственный администратор: noblepower1337
            </Text>
          </Flex>
        </MenuList>
      </Menu>

      <Button
        variant="no-hover"
        bg="transparent"
        p="0px"
        minW="unset"
        minH="unset"
        h="18px"
        w="max-content"
        onClick={toggleColorMode}
      >
        <Icon
          me="10px"
          h="18px"
          w="18px"
          color={navbarIcon}
          as={colorMode === 'light' ? IoMdMoon : IoMdSunny}
        />
      </Button>
      <Menu>
        <MenuButton p="0px">
          <Avatar
            _hover={{ cursor: 'pointer' }}
            color="white"
            name="Adela Parkson"
            bg="#11047A"
            size="sm"
            w="40px"
            h="40px"
          />
        </MenuButton>
        <MenuList
          boxShadow={shadow}
          p="0px"
          mt="10px"
          borderRadius="20px"
          bg={menuBg}
          border="none"
        >
          <Flex w="100%" mb="0px">
            <Text
              ps="20px"
              pt="16px"
              pb="10px"
              w="100%"
              borderBottom="1px solid"
              borderColor={borderColor}
              fontSize="sm"
              fontWeight="700"
              color={textColor}
            >
              👋&nbsp; Привет
            </Text>
          </Flex>
          <Flex flexDirection="column" p="10px">
            <MenuItem
              _hover={{ bg: 'none' }}
              _focus={{ bg: 'none' }}
              borderRadius="8px"
              px="14px"
            >
              <Text fontSize="sm">Настройки профиля</Text>
            </MenuItem>
            <MenuItem
              _hover={{ bg: 'none' }}
              _focus={{ bg: 'none' }}
              borderRadius="8px"
              px="14px"
            >
              <Text fontSize="sm">Настройки рассылок</Text>
            </MenuItem>
            <MenuItem
              _hover={{ bg: 'none' }}
              _focus={{ bg: 'none' }}
              color="red.400"
              borderRadius="8px"
              px="14px"
              onClick={handleLogout}
            >
              <Text fontSize="sm">Выйти</Text>
            </MenuItem>
          </Flex>
        </MenuList>
      </Menu>
    </Flex>
  );
}

HeaderLinks.propTypes = {
  variant: PropTypes.string,
  fixed: PropTypes.bool,
  secondary: PropTypes.bool,
  onOpen: PropTypes.func,
};
